<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import TracePanel from '../components/TracePanel.vue'
import {
  SIGN_RESULT_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  arriveShipment,
  canAct,
  closeShipment,
  createShipment,
  departShipment,
  listShipments,
  remainingQty,
  signShipment,
  traceShipment,
  updateShipment,
  type ShipmentInput,
  type ShipmentRow,
  type ShipmentStatus,
  type SignInput,
  type SignResult,
  type TraceBundle,
} from '../api/shipments'

const rows = ref<ShipmentRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
/** 动作（发车 / 到达 / 关闭）失败的错误：409 的原因与 trace_id 都要露出来 */
const refError = ref<unknown>(null)
const notice = ref('')

const query = reactive({
  keyword: '',
  status: '' as ShipmentStatus | '',
  trip_no: '',
  order_no: '',
  page: 1,
  size: 20,
})

const formOpen = ref(false)
const editing = ref<ShipmentRow | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive({
  trip_no: '',
  order_no: '',
  carrier: '',
  origin: '',
  dest: '',
  cargo_desc: '',
  qty: 1,
  weight_kg: 0,
  remark: '',
  expect_deliver_at: null as string | null,
})

const detailOpen = ref(false)
const detailLoading = ref(false)
const detailError = ref<unknown>(null)
const trace = ref<TraceBundle | null>(null)

const signing = ref(false)
const signError = ref<unknown>(null)
const sign = reactive({
  signee: '',
  sign_phone: '',
  result: 'signed' as SignResult,
  signed_qty: 1,
  location: '',
  remark: '',
})

const formErrorText = computed(() => {
  if (!form.origin.trim()) return '请填写起运地'
  if (!form.dest.trim()) return '请填写目的地'
  if (!(Number(form.qty) >= 1)) return '件数必须大于 0'
  if (Number(form.weight_kg) < 0) return '重量不能为负数'
  return ''
})

const signFormErrorText = computed(() => {
  if (!sign.signee.trim()) return '请填写签收人'
  if (!(Number(sign.signed_qty) >= 1)) return '本次签收数量必须大于 0'
  if (!trace.value) return ''
  const remain = remainingQty(trace.value.shipment, trace.value.pod_records)
  if (Number(sign.signed_qty) > remain) return `不能超过剩余可签 ${remain} 件`
  return ''
})

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listShipments({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
      trip_no: query.trip_no.trim() || undefined,
      order_no: query.order_no.trim() || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function resetForm(): void {
  Object.assign(form, {
    trip_no: '',
    order_no: '',
    carrier: '',
    origin: '',
    dest: '',
    cargo_desc: '',
    qty: 1,
    weight_kg: 0,
    remark: '',
    expect_deliver_at: null,
  })
}

function openCreate(): void {
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

/** 编辑只对还没发运（created）的运单开放：后端 Update 会挡下其它状态 */
function openEdit(row: ShipmentRow): void {
  editing.value = row
  resetForm()
  form.trip_no = row.trip_no
  form.order_no = row.order_no
  form.carrier = row.carrier
  form.origin = row.origin
  form.dest = row.dest
  form.cargo_desc = row.cargo_desc
  form.qty = row.qty
  form.weight_kg = Number(row.weight_kg)
  form.remark = row.remark
  formError.value = null
  formOpen.value = true
}

function buildInput(): ShipmentInput {
  return {
    trip_no: form.trip_no.trim() || undefined,
    order_no: form.order_no.trim() || undefined,
    carrier: form.carrier.trim() || undefined,
    origin: form.origin.trim(),
    dest: form.dest.trim(),
    cargo_desc: form.cargo_desc.trim() || undefined,
    qty: Number(form.qty),
    weight_kg: Number(form.weight_kg) || undefined,
    remark: form.remark.trim() || undefined,
    expect_deliver_at: form.expect_deliver_at || undefined,
  }
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input = buildInput()
  try {
    if (editing.value) {
      const updated = await updateShipment(editing.value.id, input)
      formOpen.value = false
      await load(`运单 ${updated.doc_no} 已保存`)
    } else {
      const created = await createShipment(input)
      formOpen.value = false
      await load(`运单 ${created.doc_no} 已创建`)
    }
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

function resetSign(remain = 1): void {
  Object.assign(sign, {
    signee: '',
    sign_phone: '',
    result: 'signed' as SignResult,
    signed_qty: Math.max(remain, 1),
    location: '',
    remark: '',
  })
}

async function reloadTrace(): Promise<void> {
  if (!trace.value) return
  detailError.value = null
  try {
    trace.value = await traceShipment(trace.value.shipment.id)
  } catch (e) {
    detailError.value = e
  }
}

async function openDetail(row: ShipmentRow): Promise<void> {
  detailOpen.value = true
  trace.value = null
  detailError.value = null
  signError.value = null
  resetSign()
  detailLoading.value = true
  try {
    trace.value = await traceShipment(row.id)
    resetSign(remainingQty(trace.value.shipment, trace.value.pod_records))
  } catch (e) {
    detailError.value = e
  } finally {
    detailLoading.value = false
  }
}

/** 列表行与详情里的发车 / 到达 / 关闭共用：成功后同时刷新列表与详情时间线 */
async function act(
  kind: 'depart' | 'arrive' | 'close',
  target: { id: number; doc_no: string },
): Promise<void> {
  notice.value = ''
  refError.value = null
  detailError.value = null
  try {
    if (kind === 'depart') await departShipment(target.id)
    else if (kind === 'arrive') await arriveShipment(target.id)
    else await closeShipment(target.id)
    const text = kind === 'depart' ? '已发车' : kind === 'arrive' ? '已到达' : '已关闭'
    await load(`${target.doc_no} ${text}`)
    if (detailOpen.value && trace.value?.shipment.id === target.id) await reloadTrace()
  } catch (e) {
    if (detailOpen.value && trace.value?.shipment.id === target.id) detailError.value = e
    else refError.value = e
  }
}

async function submitSign(): Promise<void> {
  if (!trace.value || signFormErrorText.value) return
  signing.value = true
  signError.value = null
  const input: SignInput = {
    signee: sign.signee.trim(),
    sign_phone: sign.sign_phone.trim() || undefined,
    result: sign.result,
    signed_qty: Number(sign.signed_qty),
    location: sign.location.trim() || undefined,
    remark: sign.remark.trim() || undefined,
  }
  const id = trace.value.shipment.id
  try {
    const out = await signShipment(id, input)
    await Promise.all([reloadTrace(), load()])
    notice.value = `${out.shipment.doc_no} 已签收 ${input.signed_qty} 件（${
      SIGN_RESULT_LABELS[input.result]
    }）`
    if (trace.value) resetSign(remainingQty(trace.value.shipment, trace.value.pod_records))
  } catch (e) {
    signError.value = e
  } finally {
    signing.value = false
  }
}
</script>

<template>
  <div class="yc-ship">
    <header class="yc-ship__bar">
      <el-input
        v-model="query.keyword"
        class="yc-ship__keyword"
        placeholder="运单号 / 行车任务号 / 订单号"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select
        v-model="query.status"
        class="yc-ship__ref"
        placeholder="全部状态"
        clearable
        @change="search"
      >
        <el-option
          v-for="(label, value) in STATUS_LABELS"
          :key="value"
          :label="label"
          :value="value"
        />
      </el-select>
      <el-input
        v-model="query.trip_no"
        class="yc-ship__ref"
        placeholder="行车任务号（精确）"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-input
        v-model="query.order_no"
        class="yc-ship__ref"
        placeholder="订单号（精确）"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'pod:shipment:create'" type="primary" @click="openCreate">
        新建运单
      </el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-ship__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-ship__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-ship__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="doc_no" label="运单号" min-width="150" fixed="left" />
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as ShipmentStatus] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as ShipmentStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="线路" min-width="170" show-overflow-tooltip>
          <template #default="{ row }">{{ row.origin || '—' }} → {{ row.dest || '—' }}</template>
        </el-table-column>
        <el-table-column label="行车任务号" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">{{ row.trip_no || '—' }}</template>
        </el-table-column>
        <el-table-column label="订单号" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">{{ row.order_no || '—' }}</template>
        </el-table-column>
        <el-table-column prop="cargo_desc" label="货描" min-width="140" show-overflow-tooltip>
          <template #default="{ row }">{{ row.cargo_desc || '—' }}</template>
        </el-table-column>
        <el-table-column label="件数（已签）" width="110" align="right">
          <template #default="{ row }">{{ row.signed_qty }} / {{ row.qty }}</template>
        </el-table-column>
        <el-table-column label="重量（kg）" width="110" align="right">
          <template #default="{ row }">{{ Number(row.weight_kg).toFixed(3) }}</template>
        </el-table-column>
        <el-table-column label="未关异常" width="90" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.open_exceptions > 0" type="danger" size="small">
              {{ row.open_exceptions }}
            </el-tag>
            <span v-else>—</span>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" min-width="150">
          <template #default="{ row }">{{ formatDateTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="300" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDetail(row)">详情</el-button>
            <el-button
              v-if="row.status === 'created'"
              v-perm="'pod:shipment:update'"
              link
              type="primary"
              @click="openEdit(row)"
            >
              编辑
            </el-button>
            <el-button
              v-if="canAct(row.status as ShipmentStatus, 'depart')"
              v-perm="'pod:shipment:depart'"
              link
              type="primary"
              @click="act('depart', row)"
            >
              发车
            </el-button>
            <el-button
              v-if="canAct(row.status as ShipmentStatus, 'arrive')"
              v-perm="'pod:shipment:arrive'"
              link
              type="primary"
              @click="act('arrive', row)"
            >
              到达
            </el-button>
            <el-button
              v-if="canAct(row.status as ShipmentStatus, 'sign')"
              v-perm="'pod:shipment:sign'"
              link
              type="success"
              @click="openDetail(row)"
            >
              签收
            </el-button>
            <el-popconfirm
              v-if="canAct(row.status as ShipmentStatus, 'close')"
              title="确认关闭该运单？存在未关闭异常时会被后端拒绝。"
              width="320"
              @confirm="act('close', row)"
            >
              <template #reference>
                <el-button v-perm="'pod:shipment:close'" link type="danger">关闭</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有运单"
        description="新建运单后会自动生成「创建运单」追溯事件，发车、到达、签收都会依次记进时间线。"
      >
        <el-button v-perm="'pod:shipment:create'" type="primary" @click="openCreate">
          新建运单
        </el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-ship__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <!-- 新建 / 编辑 -->
    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑运单 · ${editing.doc_no}` : '新建运单'"
      width="720px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-ship__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <div class="yc-ship__formrow">
          <el-form-item label="起运地" :error="formErrorText && !form.origin.trim() ? formErrorText : ''">
            <el-input v-model="form.origin" maxlength="255" placeholder="如 上海" />
          </el-form-item>
          <el-form-item
            label="目的地"
            :error="form.origin.trim() && !form.dest.trim() ? formErrorText : ''"
          >
            <el-input v-model="form.dest" maxlength="255" placeholder="如 杭州" />
          </el-form-item>
        </div>

        <div class="yc-ship__formrow">
          <el-form-item
            label="件数"
            :error="form.origin.trim() && form.dest.trim() ? formErrorText : ''"
          >
            <el-input-number v-model="form.qty" :min="1" :max="999999" controls-position="right" />
          </el-form-item>
          <el-form-item label="重量（kg）">
            <el-input-number
              v-model="form.weight_kg"
              :min="0"
              :max="999999999"
              :precision="3"
              controls-position="right"
            />
          </el-form-item>
          <el-form-item label="期望送达时间">
            <el-date-picker
              v-model="form.expect_deliver_at"
              type="datetime"
              value-format="YYYY-MM-DD HH:mm:ss"
              placeholder="可留空"
            />
          </el-form-item>
        </div>

        <div class="yc-ship__formrow">
          <el-form-item label="行车任务号">
            <el-input v-model="form.trip_no" maxlength="32" placeholder="引用 dsp 行车任务号，可留空" />
          </el-form-item>
          <el-form-item label="订单号">
            <el-input v-model="form.order_no" maxlength="32" placeholder="引用 ops 订单号，可留空" />
          </el-form-item>
          <el-form-item label="承运商">
            <el-input v-model="form.carrier" maxlength="64" placeholder="可留空" />
          </el-form-item>
        </div>

        <div class="yc-ship__formrow">
          <el-form-item label="货物描述">
            <el-input v-model="form.cargo_desc" maxlength="255" placeholder="如 日用百货 10 箱，可留空" />
          </el-form-item>
          <el-form-item label="备注">
            <el-input v-model="form.remark" maxlength="255" placeholder="可留空" />
          </el-form-item>
        </div>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建' }}
        </el-button>
        <span v-if="formErrorText" class="yc-ship__hint yc-ship__hint--inline">
          {{ formErrorText }}
        </span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>

    <!-- 详情：基本信息 + 动作 + 追溯时间线 + 签收 / 回单 / 异常 -->
    <el-dialog
      v-if="detailOpen"
      v-model="detailOpen"
      :title="trace ? `运单详情 · ${trace.shipment.doc_no}` : '运单详情'"
      width="920px"
      top="6vh"
    >
      <div v-if="detailError" class="yc-ship__error">
        <TraceIdAlert :error="detailError" />
      </div>

      <div v-loading="detailLoading">
        <template v-if="trace">
          <div class="yc-ship__acts">
            <el-button
              v-if="canAct(trace.shipment.status, 'depart')"
              v-perm="'pod:shipment:depart'"
              type="primary"
              plain
              size="small"
              @click="act('depart', trace.shipment)"
            >
              发车
            </el-button>
            <el-button
              v-if="canAct(trace.shipment.status, 'arrive')"
              v-perm="'pod:shipment:arrive'"
              type="primary"
              plain
              size="small"
              @click="act('arrive', trace.shipment)"
            >
              到达
            </el-button>
            <el-popconfirm
              v-if="canAct(trace.shipment.status, 'close')"
              title="确认关闭该运单？存在未关闭异常时会被后端拒绝。"
              width="320"
              @confirm="act('close', trace.shipment)"
            >
              <template #reference>
                <el-button v-perm="'pod:shipment:close'" type="danger" plain size="small">
                  关闭运单
                </el-button>
              </template>
            </el-popconfirm>
            <span class="yc-ship__hint">动作会写入追溯时间线，非法流转由后端返回 409。</span>
          </div>

          <TracePanel :trace="trace" />

          <section
            v-if="canAct(trace.shipment.status, 'sign')"
            v-perm="'pod:shipment:sign'"
            class="yc-ship__sign"
          >
            <h3 class="yc-ship__sect">电子签收</h3>
            <div v-if="signError" class="yc-ship__error">
              <TraceIdAlert :error="signError" />
            </div>

            <el-form label-position="top" @submit.prevent="submitSign">
              <div class="yc-ship__formrow">
                <el-form-item label="签收人" :error="!sign.signee.trim() ? signFormErrorText : ''">
                  <el-input v-model="sign.signee" maxlength="64" placeholder="如 王五" />
                </el-form-item>
                <el-form-item label="签收人电话">
                  <el-input v-model="sign.sign_phone" maxlength="32" placeholder="可留空" />
                </el-form-item>
                <el-form-item label="签收结果">
                  <el-select v-model="sign.result" class="yc-ship__full">
                    <el-option
                      v-for="(label, value) in SIGN_RESULT_LABELS"
                      :key="value"
                      :label="label"
                      :value="value"
                    />
                  </el-select>
                </el-form-item>
              </div>

              <div class="yc-ship__formrow">
                <el-form-item
                  label="本次签收数量"
                  :error="sign.signee.trim() ? signFormErrorText : ''"
                >
                  <el-input-number
                    v-model="sign.signed_qty"
                    :min="1"
                    :max="Math.max(remainingQty(trace.shipment, trace.pod_records), 1)"
                    controls-position="right"
                  />
                </el-form-item>
                <el-form-item label="签收地点">
                  <el-input v-model="sign.location" maxlength="255" placeholder="默认目的地" />
                </el-form-item>
                <el-form-item label="备注">
                  <el-input v-model="sign.remark" maxlength="255" placeholder="可留空" />
                </el-form-item>
              </div>

              <el-button
                type="primary"
                native-type="submit"
                :loading="signing"
                :disabled="!!signFormErrorText"
              >
                提交签收
              </el-button>
              <span v-if="signFormErrorText" class="yc-ship__hint yc-ship__hint--inline">
                {{ signFormErrorText }}
              </span>
              <span class="yc-ship__hint yc-ship__hint--inline">
                剩余可签 {{ remainingQty(trace.shipment, trace.pod_records) }} 件；拒收 / 破损会自动建异常并把运单置为「异常」。
              </span>
            </el-form>
          </section>
        </template>
      </div>

      <template #footer>
        <el-button @click="detailOpen = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-ship {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 220px;
  }

  &__ref {
    width: 160px;
  }

  &__full {
    width: 100%;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__hint {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.7;

    &--inline {
      margin-left: var(--yc-space-2);
    }
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__formrow {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 0 var(--yc-space-4);
  }

  &__acts {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-3);
  }

  &__sect {
    margin: var(--yc-space-5) 0 var(--yc-space-2);
    font-size: var(--yc-font);
    font-weight: 600;
  }

  &__sign {
    margin-top: var(--yc-space-5);
    padding-top: var(--yc-space-4);
    border-top: 1px solid var(--el-border-color-lighter);
  }
}
</style>
